import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Clock,
  Lightbulb,
  ShieldAlert,
  CheckCircle2,
} from "lucide-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";

import { type TourStep } from "./data";

import { cn } from "@/lib/utils";

type Rect = { top: number; left: number; width: number; height: number };
type ArrowFrom = "left" | "right" | "top" | "bottom";
type Placement = NonNullable<TourStep["placement"]>;
type PopoverLayout = {
  style: CSSProperties;
  docked: boolean;
  compact: boolean;
  arrowFrom?: ArrowFrom;
};

const PAD = 2;
const POP_W = 360;
const POP_H_EST = 420;
const GAP = 16;

function queryTourElement(selector: string | undefined): Element | null {
  if (!selector) return null;
  // Support comma-separated fallback selectors: try each in order.
  const parts = selector
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length > 1) {
    for (const part of parts) {
      const found = queryTourElement(part);
      if (found) return found;
    }
    return null;
  }
  // The SAME data-tour anchor can render in more than one place (e.g. the
  // sidebar exists as SmartSidebar, IconRail and the shadcn Sidebar — only one
  // is visible per breakpoint). querySelector returns the FIRST match, which may
  // be a hidden variant with a bogus (often too-wide) rect. Prefer the VISIBLE
  // match so the spotlight measures what the user actually sees.
  const all = safeQueryAll(document, selector);
  if (all.length === 0) return null;
  const visible = all.filter(isElementVisible);
  if (visible.length === 0) return all[0];
  // Sidebar items render as a narrow icon (collapsed) or a wider row (expanded).
  // If several variants are visible at once, pick the SMALLEST-area one so we
  // spotlight the actual nav control, never a wide wrapper/panel behind it.
  if (selector.startsWith('[data-tour="sidebar-') && visible.length > 1) {
    return visible.reduce((min, el) => (area(el) < area(min) ? el : min));
  }
  return visible[0];
}

/** Bounding-box area of an element (0 if unmeasurable). */
function area(el: Element): number {
  const r = el.getBoundingClientRect();
  return r.width * r.height;
}

/** True when the element is actually laid out and on screen (not display:none / detached). */
function isElementVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return true;
  // offsetParent is null for display:none or detached nodes (but also for
  // position:fixed, which we treat as visible if it has a box).
  const style = window.getComputedStyle(el);
  if (style.visibility === "hidden" || style.display === "none") return false;
  if (el.offsetParent === null && style.position !== "fixed") return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** querySelectorAll that never throws on a malformed selector. */
function safeQueryAll(
  root: Document | HTMLElement,
  selector: string,
): Element[] {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    if (import.meta.env.DEV)
      console.warn(`[Tour] Invalid selector: ${selector}`);
    return [];
  }
}

function resolveRect(selector: string | undefined): Rect | null {
  if (!selector) return null;
  const el = queryTourElement(selector);
  if (!el) {
    if (import.meta.env.DEV) {
      console.warn(`[Tour] Element not found: ${selector}`);
    }
    return null;
  }

  // If the target lives inside a dialog, spotlight the whole dialog so the
  // popover positions outside it — UNLESS the selector targets a small specific
  // sub-element (sidebar items, buttons) which should be spotlighted precisely.
  const isDataTourSelector = selector.startsWith("[data-tour=");
  const isSidebarTourSelector = selector.startsWith('[data-tour="sidebar-');
  // Use the parent dialog for: non-data-tour selectors, and data-tour selectors
  // that point at form/dialog container elements (not small sub-elements like buttons).
  const isFormContainerSelector =
    isDataTourSelector &&
    (selector.includes("-form") ||
      selector.includes("-dialog") ||
      selector.includes("-create-dialog"));
  const dialogEl =
    !isDataTourSelector || isFormContainerSelector
      ? el.closest('[role="dialog"]')
      : null;
  let sidebarTarget: Element | null = null;
  if (isSidebarTourSelector && el instanceof HTMLElement) {
    // Use the icon-slot span (first <span> child) if it is small (≤50 px).
    // This keeps the spotlight tightly around the icon regardless of whether the
    // SmartSidebar is collapsed (55 px) or hover-expanded (236 px).
    const firstSpan = el.querySelector(":scope > span");
    if (firstSpan instanceof HTMLElement) {
      const spanRect = firstSpan.getBoundingClientRect();
      if (spanRect.width > 0 && spanRect.width <= 50) {
        sidebarTarget = firstSpan;
      } else {
        sidebarTarget = el;
      }
    } else {
      sidebarTarget = el;
    }
  }
  const target = dialogEl ?? sidebarTarget ?? el;

  const r = target.getBoundingClientRect();
  // If the element has no dimensions (e.g. a collapsed/hidden sidebar variant),
  // do NOT balloon out to its parent — the parent is often the full-width nav
  // container, which produces a giant misplaced spotlight box. Just skip the
  // highlight for this frame; the poll/observers will re-measure once it renders.
  if (r.width === 0 && r.height === 0) {
    if (import.meta.env.DEV) {
      console.warn(`[Tour] Element has zero dimensions: ${selector}`, r);
    }
    return null;
  }
  return {
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
  };
}

function scrollToElement(selector: string | undefined): void {
  if (!selector) return;
  const el = queryTourElement(selector);
  if (el)
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
}

/** Returns true if the proposed popover position [pLeft, pTop, POP_W, POP_H_EST]
 *  overlaps the spotlighted rect. Used to pick a non-overlapping placement. */
function popOverlapsRect(
  pTop: number,
  pLeft: number,
  rect: Rect,
  width = POP_W,
  height = POP_H_EST,
): boolean {
  const pRight = pLeft + width;
  const pBottom = pTop + height;
  const rRight = rect.left + rect.width;
  const rBottom = rect.top + rect.height;
  return !(
    pRight <= rect.left ||
    pLeft >= rRight ||
    pBottom <= rect.top ||
    pTop >= rBottom
  );
}

function getDockedLayout(): PopoverLayout {
  return {
    style: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: 0,
      width: "100%",
    },
    docked: true,
    compact: false,
  };
}

function placementToArrowFrom(placement: Placement): ArrowFrom {
  if (placement === "right") return "left";
  if (placement === "left") return "right";
  if (placement === "top") return "bottom";
  return "top";
}

const NARROW_W = 320;

function getGuideMeSidebarBoundary(selector?: string): number | null {
  if (!selector?.includes("sidebar-")) return null;
  if (!window.location.pathname.startsWith("/guideme")) return null;
  const guideMeNav = document.querySelector<HTMLElement>(
    '[data-tour="guideme-nav"]',
  );
  if (!guideMeNav) return null;
  return guideMeNav.getBoundingClientRect().right + 20;
}

function calcPopoverStyle(
  rect: Rect | null,
  placement: TourStep["placement"],
  selector?: string,
): PopoverLayout {
  const guideMeMinLeft = getGuideMeSidebarBoundary(selector);
  if (!rect) {
    // If the selector targets a sidebar element, position just to the right
    // of the sidebar so the popover is never dead-center.
    if (
      selector?.includes("sidebar-") ||
      selector?.includes("defect-") ||
      selector?.includes("cycles-")
    ) {
      // Try to measure the sidebar root width for a more accurate offset.
      const sidebarRoot = document.querySelector<HTMLElement>(
        '[data-tour="sidebar-nav"]',
      );
      const sidebarRight = sidebarRoot
        ? sidebarRoot.getBoundingClientRect().right
        : 0;
      const left = Math.max(
        guideMeMinLeft ?? 64,
        sidebarRight > 0 ? sidebarRight + 16 : 280,
      );
      return {
        style: { position: "fixed", top: "30%", left },
        docked: false,
        compact: false,
        arrowFrom: "left",
      };
    }
    return {
      style: {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      },
      docked: false,
      compact: false,
    };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const clampLeft = (l: number) => Math.max(8, Math.min(l, vw - POP_W - 8));
  const clampTop = (t: number) => Math.max(8, Math.min(t, vh - POP_H_EST - 8));

  // For large elements (dialogs/full-page): try a narrow 260px side panel in
  // the left or right margin FIRST. Docked (bottom strip) is last resort because
  // it covers dialog footer buttons (Cancel / Save).
  // Narrow elements (< 400px wide) that are merely tall (e.g. sidebar rails)
  // are NOT treated as large — they use standard placement instead.
  const isLargeElement =
    (rect.width > vw * 0.55 || rect.height > vh * 0.65) && rect.width >= 400;
  if (isLargeElement) {
    // Try left margin
    if (rect.left >= NARROW_W + 16) {
      const top = Math.max(8, Math.min(rect.top, vh - POP_H_EST - 8));
      return {
        style: {
          position: "fixed",
          top,
          left: Math.max(8, rect.left - NARROW_W - GAP),
          width: NARROW_W,
        },
        docked: false,
        compact: false,
        arrowFrom: "right",
      };
    }
    // Try right margin
    const rightEdge = rect.left + rect.width;
    if (vw - rightEdge >= NARROW_W + 16) {
      const top = Math.max(8, Math.min(rect.top, vh - POP_H_EST - 8));
      return {
        style: {
          position: "fixed",
          top,
          left: rightEdge + 16,
          width: NARROW_W,
        },
        docked: false,
        compact: false,
        arrowFrom: "left",
      };
    }
    // Truly no margin → fall back to docked strip
    return getDockedLayout();
  }

  const positions: Record<Placement, { top: number; left: number }> = {
    right: {
      top: clampTop(rect.top),
      left: clampLeft(rect.left + rect.width + GAP),
    },
    left: {
      top: clampTop(rect.top),
      left: clampLeft(rect.left - POP_W - GAP),
    },
    bottom: {
      top: clampTop(rect.top + rect.height + GAP),
      left: clampLeft(rect.left + (rect.width - POP_W) / 2),
    },
    top: {
      top: clampTop(rect.top - POP_H_EST - GAP),
      left: clampLeft(rect.left + (rect.width - POP_W) / 2),
    },
  };

  const order = [placement, "right", "left", "bottom", "top"].filter(
    (value, index, array): value is Placement =>
      Boolean(value) && array.indexOf(value) === index,
  );

  for (const p of order) {
    const pos = positions[p];
    if (!popOverlapsRect(pos.top, pos.left, rect)) {
      const adjustedLeft =
        guideMeMinLeft != null ? Math.max(pos.left, guideMeMinLeft) : pos.left;
      return {
        style: { position: "fixed", top: pos.top, left: adjustedLeft },
        docked: false,
        compact: false,
        arrowFrom: placementToArrowFrom(p),
      };
    }
  }

  return getDockedLayout();
}

function ArrowIndicator({ from }: { from: ArrowFrom }) {
  const base = "absolute h-0 w-0 pointer-events-none";

  if (from === "right") {
    return (
      <div
        className={cn(base, "right-[-8px] top-6")}
        style={{
          borderTop: "8px solid transparent",
          borderBottom: "8px solid transparent",
          borderLeft: "8px solid hsl(var(--popover))",
        }}
      />
    );
  }

  if (from === "left") {
    return (
      <div
        className={cn(base, "left-[-8px] top-6")}
        style={{
          borderTop: "8px solid transparent",
          borderBottom: "8px solid transparent",
          borderRight: "8px solid hsl(var(--popover))",
        }}
      />
    );
  }

  if (from === "bottom") {
    return (
      <div
        className={cn(base, "bottom-[-8px] left-6")}
        style={{
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: "8px solid hsl(var(--popover))",
        }}
      />
    );
  }

  return (
    <div
      className={cn(base, "left-6 top-[-8px]")}
      style={{
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
        borderBottom: "8px solid hsl(var(--popover))",
      }}
    />
  );
}

export function TourOverlay({
  tourTitle,
  steps,
  initialStep = 0,
  onClose,
  onComplete,
  navigate,
}: {
  tourTitle: string;
  steps: TourStep[];
  initialStep?: number;
  onClose: (step: number) => void;
  onComplete: () => void;
  navigate: (path: string) => void;
}) {
  const activeSteps = steps;
  const [i, setI] = useState(initialStep);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = activeSteps[i];
  const total = activeSteps.length;
  const rafRef = useRef<number | null>(null);
  // When we programmatically dispatch Escape to close a dialog (dismissOnNext:"escape"),
  // we must not let our own Escape listener treat it as a "close tour" command.
  const suppressEscapeRef = useRef(false);
  const isSidebarStep =
    step.selector?.startsWith('[data-tour="sidebar-') ?? false;
  // Refs to the latest handlers so keyboard nav routes through the SAME logic
  // (autoClick, per-step navigation, dialog dismissal/reopen) as the buttons.
  const handleNextRef = useRef<() => void>(() => {});
  const handleBackRef = useRef<() => void>(() => {});

  // On mount at a non-zero step (Continue tour): if the current step's element
  // is not yet in the DOM, find the nearest preceding autoClick step and fire it
  // to reopen any dialog/panel that was left open when the user closed the tour.
  useEffect(() => {
    if (initialStep === 0) return;
    const currentStep = activeSteps[initialStep];
    if (
      !currentStep?.selector ||
      currentStep.selector.startsWith('[data-tour="sidebar-')
    )
      return;

    const reopenIfNeeded = () => {
      if (queryTourElement(currentStep.selector)) return; // element already visible
      for (let k = initialStep - 1; k >= 0; k--) {
        const s = activeSteps[k];
        if (s?.autoClick && s?.selector) {
          const btn = queryTourElement(s.selector) as HTMLElement | null;
          if (btn) {
            btn.click();
            return;
          }
        }
      }
    };
    // Give the page time to render before checking
    const t = setTimeout(reopenIfNeeded, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only on mount

  useEffect(() => {
    // Clear any previous spotlight immediately so a stale box from the last
    // step never lingers in the wrong place while the new target is measured.
    setRect(null);

    const update = () => setRect(resolveRect(step.selector));

    // Track resize / scroll for repositioning
    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    window.addEventListener("scroll", update, { passive: true, capture: true });

    // Also track sidebar/layout mutations so expanding sidebar re-spotlights correctly
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class", "style"],
    });

    // Helper: observe the actual rendered target (or dialog ancestor) so the
    // spotlight tracks element size changes — e.g. sidebar expanding on hover.
    const observeTarget = () => {
      if (!step.selector) return;
      const el = queryTourElement(step.selector) as HTMLElement | null;
      if (!el) return;
      const isDataTour = step.selector.startsWith("[data-tour=");
      const target = (!isDataTour ? el.closest('[role="dialog"]') : null) ?? el;
      ro.observe(target as Element);
    };

    // Immediate measure + scroll
    // Use rAF to ensure measurement happens after paint
    requestAnimationFrame(() => {
      update();
      scrollToElement(step.selector);
      observeTarget();
    });

    // Re-measure after scroll settles and DOM is stable
    const scrollSettleTimer = setTimeout(() => {
      scrollToElement(step.selector);
      setTimeout(update, 420);
    }, 100);

    // Continuous poll — handles elements that appear late (e.g. dialog portals
    // opened after autoClick, or opened manually by the user).
    // Fast poll (50ms) for first second, then 200ms for up to 60s total.
    let pollCount = 0;
    let pollId: ReturnType<typeof setTimeout>;
    const poll = () => {
      const r = resolveRect(step.selector);
      if (r) {
        setRect(r);
        scrollToElement(step.selector);
        observeTarget();
        return; // stop polling
      }
      pollCount++;
      if (pollCount > 320) return; // ~60s — give up
      pollId = setTimeout(poll, pollCount < 20 ? 50 : 200);
    };
    pollId = setTimeout(poll, 50);

    return () => {
      clearTimeout(scrollSettleTimer);
      clearTimeout(pollId);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", update, { capture: true });
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [step.selector, i]);

  // Pre-fill form fields with example data when the step declares prefill values.
  // Uses the native HTMLInputElement/HTMLTextAreaElement setter trick so React's
  // controlled input state is updated (plain .value= assignment is ignored by React).
  useEffect(() => {
    if (!step.prefill) return;
    const entries = Object.entries(step.prefill);
    if (entries.length === 0) return;

    const nativeInputSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )?.set;
    const nativeTextareaSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      "value",
    )?.set;

    const tryFill = () => {
      let allFilled = true;
      for (const [sel, val] of entries) {
        let el: HTMLInputElement | HTMLTextAreaElement | null = null;
        try {
          el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            sel,
          );
        } catch {
          // Invalid selector (e.g. a placeholder containing newlines) — skip it
          // rather than letting the thrown error crash the whole overlay.
          if (import.meta.env.DEV)
            console.warn(`[Tour] Invalid prefill selector: ${sel}`);
          continue;
        }
        if (!el) {
          allFilled = false;
          continue;
        }
        if (el.value === val) continue; // already correct — skip
        const setter =
          el instanceof HTMLTextAreaElement
            ? nativeTextareaSetter
            : nativeInputSetter;
        setter?.call(el, val);
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return allFilled;
    };

    // Try immediately, then retry until all fields are filled (dialog may not be mounted yet)
    if (!tryFill()) {
      let attempts = 0;
      const fillId = setInterval(() => {
        if (tryFill() || ++attempts > 50) clearInterval(fillId); // 50 × 200ms = 10s max
      }, 200);
      return () => clearInterval(fillId);
    }
  }, [step.prefill, i]);

  // Dispatch a custom event so React-state-driven components (e.g. test data
  // field arrays) can pre-populate themselves when a step declares initState.
  useEffect(() => {
    if (!step.initState) return;
    // Give the dialog/component time to mount before firing the event.
    const t = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("guideme:init-state", { detail: step.initState }),
      );
    }, 800);
    return () => clearTimeout(t);
  }, [step.initState, i]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Ignore programmatically dispatched Escape events used to close dialogs
        // (dismissOnNext: "escape") — those must not close the whole tour.
        if (suppressEscapeRef.current) return;
        onClose(i);
      }
      // Route arrow-key forward nav through handleNext so it runs autoClick,
      // per-step navigation and dialog dismissal — never a bare index bump.
      if (e.key === "ArrowRight") handleNextRef.current();
      if (e.key === "ArrowLeft") handleBackRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isLast = i === total - 1;
  const pct = Math.round(((i + 1) / total) * 100);
  const {
    style: popStyle,
    docked,
    arrowFrom,
  } = calcPopoverStyle(rect, step.placement, step.selector);

  const handleNext = () => {
    // Clear the current spotlight immediately so it never lingers on the next
    // page during navigation/dialog transitions.
    setRect(null);
    const nextIdx = i + 1;
    const nextStep = activeSteps[nextIdx];

    // Helper: advance to next step, navigating first if needed
    const advanceToNext = () => {
      if (nextStep?.route) {
        navigate(nextStep.route);
        // Give the route time to render before advancing
        setTimeout(() => setI((v) => v + 1), 400);
      } else {
        setI((v) => v + 1);
      }
    };

    if (isLast) {
      // Close any open dialog before completing the tour
      if (step.dismissOnNext) {
        if (step.dismissOnNext === "escape") {
          suppressEscapeRef.current = true;
          document.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: "Escape",
              bubbles: true,
              cancelable: true,
            }),
          );
          setTimeout(() => {
            suppressEscapeRef.current = false;
            onComplete();
          }, 350);
          return;
        }
        const dismissEl = document.querySelector<HTMLElement>(
          step.dismissOnNext,
        );
        if (dismissEl) {
          dismissEl.click();
          setTimeout(onComplete, 350);
          return;
        }
      }
      onComplete();
      return;
    }
    // If the step opened a dialog/modal, close it before advancing
    if (step.dismissOnNext) {
      if (step.dismissOnNext === "escape") {
        suppressEscapeRef.current = true;
        document.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            bubbles: true,
            cancelable: true,
          }),
        );
        setTimeout(() => {
          suppressEscapeRef.current = false;
          if (nextStep?.route) {
            navigate(nextStep.route);
            setTimeout(() => setI((v) => v + 1), 400);
          } else {
            setI((v) => v + 1);
          }
        }, 350);
        return;
      }
      const dismissEl = document.querySelector<HTMLElement>(step.dismissOnNext);
      if (dismissEl) {
        dismissEl.click();
        setTimeout(() => {
          if (nextStep?.route) {
            navigate(nextStep.route);
            setTimeout(() => setI((v) => v + 1), 400);
          } else {
            setI((v) => v + 1);
          }
        }, 350);
        return;
      }
    }
    if (step.autoClick && step.selector) {
      const el = queryTourElement(step.selector) as HTMLElement | null;
      if (el) {
        el.click();
        // Give the dialog/animation time to mount before advancing
        setTimeout(() => setI((v) => v + 1), 600);
        return;
      }
    }
    advanceToNext();
  };
  // Keep the keyboard handler pointed at the latest handleNext each render.
  handleNextRef.current = handleNext;

  // Back navigation, symmetric with Next: navigate to the correct page and
  // re-open the dialog if the target step lives inside one opened by a preceding
  // autoClick step (so going Back re-establishes the same state as Next did).
  const handleBack = () => {
    if (i === 0) return;
    // Clear the current spotlight immediately during the transition.
    setRect(null);
    const targetIdx = i - 1;
    const targetStep = activeSteps[targetIdx];

    // Page route for the target step: its own route, or the nearest preceding
    // step's route (dialog steps inherit the page from their opener step).
    let routeForTarget: string | undefined = targetStep?.route;
    if (!routeForTarget) {
      for (let k = targetIdx; k >= 0; k--) {
        if (activeSteps[k]?.route) {
          routeForTarget = activeSteps[k].route;
          break;
        }
      }
    }

    const ensureOpenerThenGo = () => {
      const needsDialog =
        targetStep?.selector &&
        !targetStep.selector.startsWith('[data-tour="sidebar-') &&
        !document.querySelector(targetStep.selector);
      if (needsDialog) {
        // Find the nearest preceding autoClick step — it's the button/tab that
        // opens the dialog/panel this step lives in (handles multi-panel tours
        // like import/export where the opener isn't the immediate prior step).
        let opener: TourStep | undefined;
        for (let k = targetIdx - 1; k >= 0; k--) {
          if (activeSteps[k]?.autoClick && activeSteps[k]?.selector) {
            opener = activeSteps[k];
            break;
          }
        }
        if (opener?.selector) {
          const btn = queryTourElement(opener.selector) as HTMLElement | null;
          if (btn) {
            btn.click();
            setTimeout(() => setI(targetIdx), 500);
            return;
          }
        }
      }
      setI(targetIdx);
    };

    if (routeForTarget) {
      navigate(routeForTarget);
      setTimeout(ensureOpenerThenGo, 400);
    } else {
      ensureOpenerThenGo();
    }
  };
  handleBackRef.current = handleBack;

  // Use the polled/snapshotted rect so sidebar hover-expansion doesn't chase
  // the spotlight. Fall back to a synchronous read only when rect is still null.
  const spotlightRect: Rect | null =
    rect ??
    (() => {
      if (!step.selector) return null;
      const el = queryTourElement(step.selector);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return null;
      return {
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
      };
    })();

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none"
      aria-label={`${tourTitle} tour`}
    >
      {spotlightRect ? (
        <div
          className="pointer-events-none absolute rounded-lg transition-[box-shadow,opacity] duration-200 ease-out"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            boxShadow: isSidebarStep
              ? "0 0 0 9999px rgba(2, 6, 23, 0.16), inset 0 0 0 2px rgba(255, 255, 255, 0.96), inset 0 0 0 5px hsl(var(--info) / 0.35), inset 0 0 24px 6px hsl(var(--info) / 0.3)"
              : "0 0 0 9999px rgba(2, 6, 23, 0.16), inset 0 0 0 2px hsl(var(--primary) / 0.9), inset 0 0 24px 4px hsl(var(--primary) / 0.18)",
            background: "transparent",
          }}
        >
          <span
            className={cn(
              "absolute inset-0 rounded-lg animate-pulse",
              isSidebarStep
                ? "ring-2 ring-inset ring-white/90"
                : "ring-2 ring-inset ring-primary/30",
            )}
          />
        </div>
      ) : (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "rgba(2, 6, 23, 0.16)" }}
        />
      )}

      {docked ? (
        <div
          key={i}
          className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[9999] overflow-hidden border-t border-border/60 bg-popover text-popover-foreground shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.1)] animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
          style={popStyle}
        >
          <div className="h-[2px] w-full bg-muted/70">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/40 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="flex items-center gap-4 px-5 py-3">
            <div className="flex shrink-0 flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                <Sparkles className="h-3 w-3 text-primary" />
                <span className="max-w-[180px] truncate">{tourTitle}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary tabular-nums">
                  {i + 1} / {total}
                </span>
                {total <= 8 ? (
                  <div className="flex items-center gap-1" aria-hidden="true">
                    {Array.from({ length: total }).map((_, index) => (
                      <span
                        key={index}
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          index === i ? "bg-primary" : "bg-muted-foreground/20",
                        )}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="h-8 w-px shrink-0 bg-border/60" />

            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold tracking-tight">
                {step.title}
              </div>
              <div className="mt-1 line-clamp-2 text-[11.5px] leading-[1.55] text-muted-foreground">
                {step.description}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              <button
                onClick={() => onClose(i)}
                className="text-[11px] font-medium text-muted-foreground/70 transition-colors hover:text-foreground"
              >
                Skip
              </button>
              <button
                disabled={i === 0}
                onClick={handleBack}
                className={cn(
                  "inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted",
                  "disabled:pointer-events-none disabled:opacity-40",
                )}
              >
                <ChevronLeft className="h-3 w-3" />
                Back
              </button>
              <button
                onClick={handleNext}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {isLast ? "Finish" : "Next"}
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          key={i}
          className={cn(
            "pointer-events-auto fixed z-[9999] overflow-hidden rounded-xl border-0 bg-popover text-popover-foreground shadow-[0_12px_48px_-8px_rgba(0,0,0,0.22),0_4px_12px_-2px_rgba(0,0,0,0.10)] animate-in fade-in-0 zoom-in-95 duration-200",
            popStyle.width === NARROW_W ? "w-[320px]" : "w-[360px]",
          )}
          style={
            popStyle.width === NARROW_W ? { ...popStyle, width: 320 } : popStyle
          }
        >
          {arrowFrom ? <ArrowIndicator from={arrowFrom} /> : null}

          {/* Dark branded header — progress bar embedded at bottom */}
          <div className="relative overflow-hidden rounded-t-xl bg-foreground px-4 py-3.5">
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10">
              <div
                className="h-full bg-white/60 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 shrink-0 text-white/40" />
                  <span className="truncate text-[10px] font-medium uppercase tracking-[0.18em] text-white/40">
                    {tourTitle}
                  </span>
                </div>
                <div className="mt-2.5 flex items-center gap-2.5">
                  {total <= 10 ? (
                    <div className="flex items-center gap-1" aria-hidden="true">
                      {Array.from({ length: total }).map((_, index) => (
                        <span
                          key={index}
                          className={cn(
                            "rounded-full transition-all duration-200",
                            index === i
                              ? "h-2 w-4 bg-white"
                              : index < i
                                ? "h-1.5 w-1.5 bg-white/45"
                                : "h-1.5 w-1.5 bg-white/15",
                          )}
                        />
                      ))}
                    </div>
                  ) : null}
                  <span className="text-[11px] font-medium tabular-nums text-white/50">
                    {i + 1} / {total}
                  </span>
                </div>
              </div>
              <button
                onClick={() => onClose(i)}
                className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md text-white/30 transition-colors hover:bg-white/10 hover:text-white/70"
                aria-label="Close tour"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="max-h-[min(52vh,420px)] overflow-y-auto px-4 pt-3 pb-4 scrollbar-thin">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              {step.eta ? (
                <>
                  <Clock className="h-3 w-3" />
                  <span>{step.eta}</span>
                </>
              ) : (
                <span>Guided walkthrough</span>
              )}
            </div>

            <h3 className="mt-1 text-[14px] font-semibold tracking-tight text-foreground">
              {step.title}
            </h3>
            <p className="mt-2 text-[12.5px] leading-[1.6] text-foreground/80">
              {step.description}
            </p>

            {step.purpose ? (
              <div className="mt-2 rounded-r-md border-l-2 border-border bg-muted/40 pl-3 pr-3 py-2 text-[11.5px] leading-[1.55] text-muted-foreground">
                {step.purpose}
              </div>
            ) : null}

            {step.tip ? (
              <div className="mt-2 flex gap-2 rounded-r-md border-l-2 border-primary/50 bg-primary/[0.04] pl-3 pr-3 py-2 text-[11.5px] leading-[1.55]">
                <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                <span className="text-foreground/85">
                  <b className="font-semibold text-foreground">Tip.</b>{" "}
                  {step.tip}
                </span>
              </div>
            ) : null}

            {step.bestPractice ? (
              <div className="mt-2 flex gap-2 rounded-r-md border-l-2 border-emerald-500/60 bg-emerald-500/[0.04] pl-3 pr-3 py-2 text-[11.5px] leading-[1.55]">
                <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                <span className="text-foreground/85">
                  <b className="font-semibold text-foreground">
                    Best practice.
                  </b>{" "}
                  {step.bestPractice}
                </span>
              </div>
            ) : null}

            {step.warning ? (
              <div className="mt-2 flex gap-2 rounded-r-md border-l-2 border-amber-500/60 bg-amber-500/[0.04] pl-3 pr-3 py-2 text-[11.5px] leading-[1.55]">
                <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                <span className="text-foreground/85">
                  <b className="font-semibold text-foreground">Heads up.</b>{" "}
                  {step.warning}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border/60 px-4 py-2.5">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onClose(i)}
                className="text-[11px] font-medium text-muted-foreground/70 transition-colors hover:text-foreground"
              >
                Skip
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={i === 0}
                onClick={handleBack}
                className={cn(
                  "inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted",
                  "disabled:pointer-events-none disabled:opacity-40",
                )}
              >
                <ChevronLeft className="h-3 w-3" />
                Back
              </button>
              <button
                onClick={handleNext}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {isLast ? "Finish" : "Next"}
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
